import React, { useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  Typography,
  useMediaQuery,
} from "@mui/material";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";

import Modal from "Components/Common/Modal";
import InputFieldText from "Components/Common/InputFieldText";
import Backdrop from "Components/Common/Backdrop";
import { getOptionValue } from "common/enums";
import SelectField from "Components/Common/SelectField";
import {
  applicationTypes,
  otherMeterId,
  otherUtilityMeterReadingUnits,
} from "common/constant";

import { filterEmptyStrings } from "utils";

import { useLazyGetOtherApplicationMeterDetailsQuery } from "apis/otherApplicationDetails";
import {
  useGetAvailableMetersQuery,
  useGetConsumerOtherMeterDetailsByConsumerId,
  usePostOtherUtilityMeterInfoMutation,
} from "apis/UtilityConsumerMeter";

const otherOption = {
  label: "Other",
  value: otherMeterId,
};

const MeterInfoForm = ({
  t,
  applicationTypeId,
  applicationData: { applicationId, meterId, consumerId, utilityId, tariffId },
  handleClose,
}) => {
  const [addMeterInfo, { isLoading }] = usePostOtherUtilityMeterInfoMutation();

  const { data: { data: utilityConsumerData } = {} } =
    useGetConsumerOtherMeterDetailsByConsumerId(consumerId);
  const {
    data: { data: availableMeters } = {},
    isLoading: isMetersLoading,
    isFetching: isMetersFetching,
  } = useGetAvailableMetersQuery({
    applicationId: applicationId ?? "",
    utilityConsumerId: consumerId,
    utility: utilityId,
  });

  const [
    getMeterDetails,
    {
      data: { data: meterDetails } = {},
      isLoading: isMeterDetailsLoading,
      isFetching: isMeterDetailsFetching,
    },
  ] = useLazyGetOtherApplicationMeterDetailsQuery();

  const isMobileDevice = useMediaQuery("(max-width: 768px)");

  const meters = useMemo(
    () =>
      availableMeters?.map(({ srNo, id }) => ({ label: srNo, value: id })) ??
      [],
    [availableMeters]
  );

  const { isCommercialTariff, isResidentialTariff } = useMemo(
    () => ({
      isCommercialTariff: tariffId === getOptionValue("tariff", "Commercial"),
      isResidentialTariff: tariffId === getOptionValue("tariff", "Residential"),
    }),
    [tariffId]
  );

  const isRemovalApplication = useMemo(
    () => applicationTypes["Removal of Connection"] === applicationTypeId,
    [applicationTypeId]
  );

  const isDcsUtility = useMemo(
    () => utilityId === getOptionValue("utilityOptions", "DCS"),
    [utilityId]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      utilityId,
      meter: {
        id: meterDetails?.meter?.id ?? utilityConsumerData?.meter?.id ?? 0,
        srNo:
          meterDetails?.meter?.srNo ?? utilityConsumerData?.meter?.srNo ?? "",
        make:
          meterDetails?.meter?.make ?? utilityConsumerData?.meter?.make ?? "",
        modelNumber:
          meterDetails?.meter?.modelNumber ??
          utilityConsumerData?.meter?.modelNumber ??
          "",
      },
      initialMeterReadingDetail: {
        id: meterDetails?.initialMeterReadingDetail?.id ?? 0,
        reading: meterDetails?.initialMeterReadingDetail?.reading ?? "",
        actualMaxDemand:
          meterDetails?.initialMeterReadingDetail?.actualMaxDemand ?? "",
        proRataConsumption:
          meterDetails?.initialMeterReadingDetail?.proRataConsumption ?? "",
      },
      finalMeterReadingDetail: {
        id: meterDetails?.finalMeterReadingDetail?.id ?? 0,
        reading: meterDetails?.finalMeterReadingDetail?.reading ?? "",
        actualMaxDemand:
          meterDetails?.finalMeterReadingDetail?.actualMaxDemand ?? "",
        proRataConsumption:
          meterDetails?.finalMeterReadingDetail?.proRataConsumption ?? "",
        note: meterDetails?.finalMeterReadingDetail?.note ?? "",
        returnWaterTemperaturePenaltyCharges:
          meterDetails?.finalMeterReadingDetail
            ?.returnWaterTemperaturePenaltyCharges ?? "",
        isDemandCharge:
          meterDetails?.finalMeterReadingDetail?.isDemandCharge ?? "",
      },
    },
    validationSchema: Yup.object({
      meter: Yup.object({
        srNo: Yup.string().required("Serial number is required"),
        make: Yup.string().required("Make is required"),
        modelNumber: Yup.string().required("Model number is required"),
      }),
      initialMeterReadingDetail: Yup.object({
        reading: Yup.string().test("isInitialReadingRequired", "", function () {
          const [
            { value: current = null } = {},
            { value: parent = null } = {},
          ] = this.from || [];
          if (
            (meterId !== parent.meter.id || !!applicationTypeId) &&
            !current.reading &&
            current.reading !== 0 &&
            !isRemovalApplication
          ) {
            return this.createError({
              message: "Initial reading is required",
            });
          }
          return true;
        }),
      }),

      finalMeterReadingDetail: Yup.object({
        reading: Yup.string().test(
          "isFinalMeterReadingRequired",
          "",
          function () {
            const [
              { value: current = null } = {},
              { value: parent = null } = {},
            ] = this.from || [];

            if (
              ((meterId !== parent.meter.id && !applicationTypeId) ||
                isRemovalApplication) &&
              current.reading !== 0 &&
              !current.reading
            ) {
              return this.createError({
                message: "Final reading is required",
              });
            }
            return true;
          }
        ),
        actualMaxDemand: Yup.string().test(
          "isFinalMeterActualMaxDemandRequired",
          "",
          function () {
            const [
              { value: current = null } = {},
              { value: parent = null } = {},
            ] = this.from || [];

            if (
              ((meterId !== parent.meter.id && !applicationTypeId) ||
                isRemovalApplication) &&
              isDcsUtility &&
              isCommercialTariff &&
              current.actualMaxDemand !== 0 &&
              !current.actualMaxDemand
            ) {
              return this.createError({
                message: "Actual max demand is required",
              });
            }
            return true;
          }
        ),
        proRataConsumption: Yup.string().test(
          "isFinalMeterProRataConsumptionRequired",
          "",
          function () {
            const [
              { value: current = null } = {},
              { value: parent = null } = {},
            ] = this.from || [];

            if (
              ((meterId !== parent.meter.id && !applicationTypeId) ||
                isRemovalApplication) &&
              isDcsUtility &&
              current.proRataConsumption !== 0 &&
              !current.proRataConsumption
            ) {
              return this.createError({
                message: "Pro Rata consumption is required",
              });
            }
            return true;
          }
        ),
        returnWaterTemperaturePenaltyCharges: Yup.string().test(
          "isFinalMeterProRataConsumptionRequired",
          "",
          function () {
            const [
              { value: current = null } = {},
              { value: parent = null } = {},
            ] = this.from || [];

            if (
              ((meterId !== parent.meter.id && !applicationTypeId) ||
                isRemovalApplication) &&
              isCommercialTariff &&
              isDcsUtility &&
              current.returnWaterTemperaturePenaltyCharges !== 0 &&
              !current.returnWaterTemperaturePenaltyCharges
            ) {
              return this.createError({
                message: "Return water temperature penalty charge is required",
              });
            }
            return true;
          }
        ),
      }),
    }),
    onSubmit: async (values) => {
      try {
        const finalPayload = Object.entries(filterEmptyStrings(values)).reduce(
          (res, [key, value]) => {
            if (
              typeof value !== "object" ||
              value instanceof File ||
              Object.keys(value).length > 1
            ) {
              return { ...res, [key]: value };
            }
            return res;
          },
          {}
        );

        await addMeterInfo({
          payload: finalPayload,
          toastMessage: `Meter details ${
            meterId ? "updated" : "added"
          } successfully`,
          query: filterEmptyStrings({
            applicationId: applicationId ?? "",
            utilityConsumerId: consumerId ?? "",
          }),
        });
        handleClose();
      } catch (error) {}
    },
  });

  const {
    handleSubmit,
    setFieldValue,
    values: {
      meter: { id },
      finalMeterReadingDetail: { isDemandCharge },
    },
  } = formik;

  useEffect(() => {
    if (meterId) {
      (async () => await getMeterDetails(meterId))();
    }
  }, [meterId]);

  useEffect(() => {
    if (id) {
      (async () => await getMeterDetails(id))();
    }
  }, [id]);

  if (
    isMeterDetailsLoading ||
    isMetersLoading ||
    isMetersFetching ||
    isMeterDetailsFetching
  ) {
    <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title={t("Add/Update Meter Info")}
      maxWidth="md"
    >
      <Grid container className="mt-3" columnSpacing={2}>
        {!isRemovalApplication && (
          <Grid item xs={12} md={4} sm={6}>
            <SelectField
              formik={formik}
              label={t("Meter Number")}
              name="meter.id"
              options={[...meters, otherOption]}
              onChange={(_, { value }) => {
                setFieldValue("meter.id", value);
                if (value === otherMeterId) {
                  setFieldValue("meter.modelNumber", "");
                  setFieldValue("meter.make", "");
                  setFieldValue("meter.srNo", "");
                  setFieldValue("initialMeterReadingDetail.reading", "");
                  setFieldValue("finalMeterReadingDetail.reading", "");
                  if (!applicationTypeId) {
                    setFieldValue("initialMeterReadingDetail.id", 0);
                    setFieldValue("finalMeterReadingDetail.id", 0);
                  }
                }
              }}
              value={meters.find(({ value }) => value === id) ?? otherOption}
              loading={isLoading}
            />
          </Grid>
        )}
        <Grid item xs={12} md={4} sm={6}>
          <InputFieldText
            label={t("Serial Number") + "*"}
            name="meter.srNo"
            formik={formik}
            placeholder="Enter Serial Number"
          />
        </Grid>
        <Grid item xs={12} md={4} sm={6}>
          <InputFieldText
            label={t("Make") + "*"}
            name="meter.make"
            formik={formik}
            placeholder="Enter Make"
          />
        </Grid>
        <Grid item xs={12} md={4} sm={6}>
          <InputFieldText
            label={t("Model Number") + "*"}
            name="meter.modelNumber"
            formik={formik}
            placeholder="Enter Model Number"
          />
        </Grid>
        {(!!applicationTypeId || meterId !== id) && !isRemovalApplication && (
          <>
            <Grid item xs={12}>
              <Typography className="mb-3" lineHeight="1" variant="h6">
                Initial Reading
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sm={6}>
              <InputFieldText
                label={t("Initial Reading") + "*"}
                name="initialMeterReadingDetail.reading"
                formik={formik}
                type="number"
                placeholder="Enter Initial Reading"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {otherUtilityMeterReadingUnits[utilityId]}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </>
        )}
        {((!applicationTypeId && meterId !== id) || isRemovalApplication) && (
          <>
            <Grid item xs={12}>
              <Typography className="mb-3" lineHeight="1" variant="h6">
                Final Reading
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sm={6}>
              <InputFieldText
                label={t("Final Reading") + "*"}
                name="finalMeterReadingDetail.reading"
                formik={formik}
                type="number"
                placeholder="Enter Final Reading*"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {otherUtilityMeterReadingUnits[utilityId]}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {isDcsUtility && (
              <>
                {((!!applicationId && isRemovalApplication) ||
                  !applicationId) && (
                  <>
                    <Grid item xs={12} md={4} sm={6}>
                      <InputFieldText
                        label={t("Pro Rata Consumption") + "*"}
                        name="finalMeterReadingDetail.proRataConsumption"
                        formik={formik}
                        type="number"
                        placeholder="Enter Pro Rata Consumption"
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sm={6}>
                      <InputFieldText
                        label={t("Note") + "*"}
                        name="finalMeterReadingDetail.note"
                        formik={formik}
                        placeholder="Enter Note"
                      />
                    </Grid>
                  </>
                )}

                {isCommercialTariff &&
                  (isRemovalApplication || !applicationId) && (
                    <>
                      <Grid item xs={12} md={4} sm={6}>
                        <InputFieldText
                          label={
                            t("Return Water Temperature Penalty Charge ") + "*"
                          }
                          name="finalMeterReadingDetail.returnWaterTemperaturePenaltyCharges"
                          formik={formik}
                          type="number"
                          placeholder="Enter Return Water Temperature Penalty Charge "
                        />
                      </Grid>
                      <Grid item xs={12} md={4} sm={6}>
                        <InputFieldText
                          label={t("Actual Max Demand") + "*"}
                          name="finalMeterReadingDetail.actualMaxDemand"
                          formik={formik}
                          type="number"
                          placeholder="Enter Actual Max Demand"
                        />
                      </Grid>
                    </>
                  )}
                {isResidentialTariff &&
                  (isRemovalApplication || !applicationId) && (
                    <>
                      <Grid item xs={12} md={4} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={!!isDemandCharge}
                              onChange={() =>
                                setFieldValue(
                                  "finalMeterReadingDetail.isDemandCharge",
                                  !isDemandCharge
                                )
                              }
                            />
                          }
                          label={t("Demand Charge")}
                        />
                      </Grid>
                    </>
                  )}
              </>
            )}
          </>
        )}
      </Grid>
      <Grid className="d-block text-end" mt={1}>
        <Button
          color="primary"
          className={isMobileDevice ? "mt-2" : "mb-2"}
          variant="contained"
          type="submit"
          disabled={isLoading}
          onClick={handleSubmit}
        >
          Submit {isLoading && <CircularProgress size={20} />}
        </Button>
      </Grid>
    </Modal>
  );
};

MeterInfoForm.propTypes = {
  applicationData: PropTypes.object,
  handleClose: PropTypes.func,
  applicationTypeId: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(MeterInfoForm);
